import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { createStyles, Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import RjsfForm, { IChangeEvent, ISubmitEvent, FormProps as RjsfFormProps } from "@rjsf/core";
import classNames from "classnames";
import React from "react";
import { GenericWithStyles } from "../models";
import { Field, fieldRegistry } from "./fields";
import { FormAction, FormContext, FormValidation, JsonSchema, ValidationError } from "./models";
import { ArrayFieldTemplate, FieldTemplate, ObjectFieldTemplate } from "./templates";
import { applyValidationErrors, getValidationErrors } from "./utils";
import { Widget, widgetRegistry } from "./widgets";

const styles = (theme: Theme) =>
  createStyles({
    form: {
      margin: 0,
      "& > .field": {
        margin: 0,
      },
      "& fieldset": {
        margin: 0,
        padding: 0,
        border: "none",
      },
      "& .field-object": {
        margin: 0,
        "& .properties": {
          marginTop: 0,
          marginBottom: 0,
        },
        "& .property": {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
      "& .field--hidden": {
        display: "none",
      },
      // Collapse top margin for overriding label (i.e. Checkbox/Consent Widget)
      "& .field-label--hidden > label": {
        marginTop: 0,
      },
    },
    formActions: {
      display: "flex",
      flexDirection: "row",
      margin: `0 ${-theme.spacing(0.5)}px`,
      paddingTop: theme.spacing(2),
      "& button": {
        margin: `0 ${theme.spacing(0.5)}px`,
      },
    },
    formActionsSticky: {
      position: "sticky",
      bottom: 0,
      backgroundColor: theme.palette.background.default,
      marginTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      marginBottom: -theme.spacing(2),
      borderTop: `solid 1px ${theme.palette.divider}`,
      boxShadow: `0px -10px 10px -10px ${theme.palette.divider}`,
      zIndex: 100,
    },
    formActionsRightAlignment: {
      flexDirection: "row-reverse",
    },
    formActionSubmitButtonWrapper: {
      position: "relative",
    },
    formActionSubmitButtonProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
  });

export interface FormProps<T>
  extends Omit<RjsfFormProps<T>, "schema" | "widgets" | "fields" | "validate" | "transformErrors">,
    WithStyles<typeof styles> {
  tagName?: keyof JSX.IntrinsicElements | React.ComponentType;
  schema: JsonSchema;
  widgets?: { [name: string]: Widget<any> };
  fields?: { [name: string]: Field<any> };
  formContext?: FormContext;
  formActions?: FormAction[];
  formActionsSticky?: boolean;
  formActionsAlignment?: "left" | "right";
  formActionsClassName?: string;
  includeExtraData?: boolean;
  isFormSubmitting?: boolean;
  validate?: (formData: T, errors: FormValidation<T>, isSubmit?: boolean) => void;
  transformErrors?: (formData: T, schema: JsonSchema, errors: ValidationError[]) => ValidationError[];
  onBlur?: (blurredPropertyId: string, blurredPropertyValue: any) => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
}

export const FormComponent = class<T> extends React.Component<FormProps<T>> {
  public render() {
    const {
      children,
      id,
      tagName,
      formData,
      schema,
      uiSchema,
      widgets: customWidgets,
      fields: customFields,
      formContext,
      formActions: formActionsSpec,
      formActionsSticky,
      formActionsAlignment,
      includeExtraData,
      isFormSubmitting,
      className,
      classes,
      onEdit,
      onClose,
      onCancel,
      onError,
    } = this.props;

    const widgets = customWidgets ? { ...widgetRegistry, ...customWidgets } : widgetRegistry;
    const fields = customFields ? { ...fieldRegistry, ...customFields } : fieldRegistry;

    const formActions: FormAction[] = [];
    // Extend each of the FormActions as applicable...
    if (formActionsSpec) {
      formActionsSpec.forEach(formActionSpec => {
        switch (formActionSpec.type) {
          case "edit":
          case "cancel":
          case "close":
            const onClickActionMap = {
              edit: onEdit,
              cancel: onCancel,
              close: onClose,
            };
            formActions.push({
              ...formActionSpec,
              button: {
                ...formActionSpec.button,
                onClick: onClickActionMap[formActionSpec.type],
              },
            });
            break;
          case "submit":
            formActions.push({
              ...formActionSpec,
              button: {
                ...formActionSpec.button,
                disabled: formActionSpec.button.disabled || isFormSubmitting,
                onClick: (event: React.ChangeEvent<HTMLInputElement>) => {
                  if (!formContext) {
                    throw Error("formContext not found.");
                  }

                  formContext.submitValidationEnabled = true;
                },
              },
            });
            break;
          default:
            formActions.push({
              ...formActionSpec,
              button: { ...formActionSpec.button },
            });
            break;
        }
      });
    }

    const hasFormActions = formActions && formActions.length > 0;
    // If the form can be submitted, ensure validation is enabled
    const enableValidation = hasFormActions ? formActions.findIndex(fa => fa.type === "submit") !== -1 : false;

    const formActionsClassName = classNames(
      classes.formActions,
      { [classes.formActionsSticky]: formActionsSticky === true },
      { [classes.formActionsRightAlignment]: formActionsAlignment === "right" },
      this.props.formActionsClassName,
    );

    return (
      <RjsfForm
        id={id}
        tagName={tagName}
        schema={schema}
        uiSchema={uiSchema}
        formData={formData}
        formContext={formContext}
        widgets={widgets as any}
        fields={fields as any}
        FieldTemplate={FieldTemplate}
        ObjectFieldTemplate={ObjectFieldTemplate}
        ArrayFieldTemplate={ArrayFieldTemplate as any}
        liveValidate={true}
        noHtml5Validate={true}
        noValidate={!enableValidation}
        validate={enableValidation ? (this.handleValidate as any) : undefined}
        transformErrors={this.handleTransformErrors as any}
        showErrorList={false}
        omitExtraData={!includeExtraData}
        className={classNames(classes.form, className)}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        onSubmit={this.handleSubmit}
        onError={onError}
      >
        {children}
        <div className={formActionsClassName} style={!hasFormActions ? { display: "none" } : undefined}>
          {hasFormActions &&
            formActions!.map((formAction: FormAction, index: number) =>
              formAction.type === "submit" ? (
                <div key={`fab_${index}`} className={classes.formActionSubmitButtonWrapper}>
                  <Button
                    id={formAction.button.id}
                    type="submit"
                    variant={formAction.button.variant === "primary" ? "contained" : undefined}
                    color={formAction.button.variant === "primary" ? "primary" : undefined}
                    className={formAction.button.className}
                    style={formAction.button.style}
                    disabled={formAction.button.disabled}
                    onClick={formAction.button.onClick}
                  >
                    {formAction.button.label}
                  </Button>
                  {!!isFormSubmitting && (
                    <CircularProgress size={24} className={classes.formActionSubmitButtonProgress} />
                  )}
                </div>
              ) : (
                <Button
                  key={`fab_${index}`}
                  id={formAction.button.id}
                  type="button"
                  variant={formAction.button.variant === "primary" ? "contained" : undefined}
                  color={formAction.button.variant === "primary" ? "primary" : undefined}
                  className={formAction.button.className}
                  style={formAction.button.style}
                  disabled={formAction.button.disabled}
                  onClick={formAction.button.onClick}
                >
                  {formAction.button.label}
                </Button>
              ),
            )}
        </div>
      </RjsfForm>
    );
  }

  public handleBlur = (blurredPropertyId: string, blurredPropertyValue: any) => {
    const { onBlur } = this.props;

    if (onBlur) {
      onBlur(blurredPropertyId, blurredPropertyValue);
    }
  };

  public handleChange = (formChangeEvent: IChangeEvent<T>) => {
    const { onChange } = this.props;

    if (onChange) {
      onChange(formChangeEvent);
    }
  };

  public handleSubmit = (formSubmitEvent: ISubmitEvent<T>) => {
    const { onSubmit } = this.props;

    if (onSubmit) {
      onSubmit(formSubmitEvent);
    }
  };

  public handleValidate = (formData: T, errors: FormValidation<T>) => {
    const { formContext, validate } = this.props;

    if (!formContext) {
      throw Error("formContext not found.");
    }

    if (validate) {
      validate(formData, errors, formContext.submitValidationEnabled);
    }

    if (formContext.submitValidationEnabled) {
      formContext.submitValidationEnabled = false;
    }

    const sharedValidationErrors = formContext.sharedValidation.getErrors();
    // If there were any widgets or fields that have added to the sharedValidationErrors on the formContext, transfer
    // them to the overall validation errors list
    if (Object.keys(sharedValidationErrors).length) {
      applyValidationErrors(sharedValidationErrors, errors);
    }

    return errors;
  };

  public handleTransformErrors = (formData: T, schema: JsonSchema, errors: ValidationError[]): ValidationError[] => {
    const { formContext } = this.props;

    if (!formContext) {
      throw Error("formContext not found.");
    }

    return getValidationErrors(formData, schema, formContext, errors);
  };
};

export const Form = withStyles(styles)(FormComponent) as <T>(
  props: GenericWithStyles<FormProps<T>>,
) => React.ReactElement;
